import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Heartbeat, Clock, User, Users, CalendarBlank, ArrowRight,
  CalendarCheck, ArrowsClockwise,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthModal } from "@/components/AuthModal";
import { BrandLogo } from "@/components/BrandLogo";

const GYMMASTER_BOOK_URL = "https://otorohangahf.gymmasteronline.com/portal/account/book/class/schedule";

function relativeTime(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const mins = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (mins < 1) return "just now";
  if (mins === 1) return "1 min ago";
  if (mins < 60) return `${mins} mins ago`;
  const hrs = Math.round(mins / 60);
  if (hrs === 1) return "1 hour ago";
  if (hrs < 24) return `${hrs} hours ago`;
  const days = Math.round(hrs / 24);
  return days === 1 ? "1 day ago" : `${days} days ago`;
}

function formatDayLabel(dateStr, dayName) {
  if (!dateStr) return dayName || "";
  const d = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) return dayName || dateStr;
  const day = dayName || d.toLocaleDateString("en-NZ", { weekday: "long" });
  const rest = d.toLocaleDateString("en-NZ", { day: "numeric", month: "short" });
  return `${day}, ${rest}`;
}

export default function Classes() {
  const [classes, setClasses] = useState([]);
  const [lastSynced, setLastSynced] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authOpen, setAuthOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const c = await api.get("/classes");
      const payload = c.data;
      setClasses(Array.isArray(payload) ? payload : (payload.items || []));
      setLastSynced(Array.isArray(payload) ? null : payload.last_synced);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Group by date chronologically (each session has a real date + spots from GymMaster).
  const groups = Object.values(
    classes.reduce((acc, c) => {
      const key = c.date || c.day || "unknown";
      if (!acc[key]) acc[key] = { key, date: c.date, day: c.day, items: [] };
      acc[key].items.push(c);
      return acc;
    }, {})
  )
    .map((g) => ({ ...g, items: g.items.sort((a, b) => (a.sort_key || a.time || "").localeCompare(b.sort_key || b.time || "")) }))
    .sort((a, b) => (a.key || "").localeCompare(b.key || ""));

  return (
    <div className="bg-white text-zinc-950 min-h-screen">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-zinc-200/60">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <BrandLogo testId="classes-logo" onClick={() => navigate("/")} />
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/")} className="hidden sm:inline-flex items-center gap-2 text-sm font-bold hover:text-[#FF4F00] transition-colors" data-testid="classes-back">
              <ArrowLeft size={16} weight="bold" /> Home
            </button>
            <button onClick={() => navigate("/memberships")} className="text-sm font-bold hover:text-[#FF4F00] transition-colors">Memberships</button>
            {user ? (
              <button onClick={() => navigate("/dashboard")} data-testid="classes-account" className="bg-zinc-950 text-white rounded-sm px-5 py-2.5 text-sm font-bold hover:bg-zinc-800 transition-colors">My Account</button>
            ) : (
              <button onClick={() => setAuthOpen(true)} data-testid="classes-signin" className="bg-[#FF4F00] text-white rounded-sm px-5 py-2.5 text-sm font-bold hover:bg-[#E04600] transition-colors">Sign in</button>
            )}
          </div>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-8">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF4F00] mb-3">Reformer Pilates Timetable</p>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-none max-w-3xl">
          Book your spot on the reformer.
        </h1>
        <p className="mt-5 text-lg text-zinc-600 max-w-xl">
          Small-group Reformer Pilates with Erin. Live availability below, straight from GymMaster — grab a spot before it fills.
        </p>
        {lastSynced && (
          <div className="mt-6 inline-flex items-center gap-2 text-xs font-bold text-zinc-500 bg-zinc-100 rounded-full px-3 py-1.5" data-testid="classes-last-synced">
            <ArrowsClockwise size={14} weight="bold" className="text-green-600" />
            Availability synced with GymMaster {relativeTime(lastSynced)}
          </div>
        )}
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-4">
        <div className="rounded-sm bg-zinc-950 text-white p-8 sm:p-10 flex flex-col md:flex-row md:items-center gap-6 justify-between" data-testid="gymmaster-booking-section">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-sm bg-[#FF4F00] flex items-center justify-center shrink-0">
              <CalendarCheck size={24} weight="bold" />
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF4F00] mb-1">Book on GymMaster</p>
              <h2 className="font-heading text-2xl font-black tracking-tight">Reserve your session in the GymMaster portal</h2>
              <p className="text-sm text-zinc-400 mt-2 max-w-md">
                Members book and manage all class sessions directly through GymMaster's live schedule.
              </p>
            </div>
          </div>
          <a
            href={GYMMASTER_BOOK_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="gymmaster-book-btn"
            className="inline-flex items-center justify-center gap-2 bg-[#FF4F00] text-white rounded-sm px-7 py-4 font-bold tracking-wide hover:bg-[#E04600] transition-colors shrink-0"
          >
            Book a session <ArrowRight size={18} weight="bold" />
          </a>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24 space-y-10">
        {!loading && groups.length === 0 && (
          <div className="bg-white border border-dashed border-zinc-300 rounded-sm p-12 text-center text-zinc-400 font-bold">
            No classes scheduled yet — check back soon.
          </div>
        )}
        {loading && (
          <div className="bg-white border border-dashed border-zinc-300 rounded-sm p-12 text-center text-zinc-400 font-bold" data-testid="classes-loading">
            Loading the latest availability…
          </div>
        )}
        {groups.map((g, gi) => (
          <div key={g.key}>
            <div className="flex items-center gap-3 mb-4">
              <CalendarBlank size={20} weight="bold" className="text-[#FF4F00]" />
              <h2 className="font-heading text-2xl font-black tracking-tight">{formatDayLabel(g.date, g.day)}</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {g.items.map((c, i) => {
                const capacity = c.capacity ?? 0;
                const spots = c.spots_free != null ? c.spots_free : Math.max((c.capacity || 0) - (c.booked_count || 0), 0);
                const booked = c.booked_count != null ? c.booked_count : Math.max((c.capacity || 0) - spots, 0);
                const full = spots <= 0;
                return (
                  <motion.div
                    key={c.id}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: (gi * 3 + i) * 0.04 }}
                    className="bg-white border border-zinc-200 rounded-sm p-5 hover-lift flex flex-col"
                    data-testid={`class-card-${c.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="w-11 h-11 rounded-sm bg-[#FF4F00] flex items-center justify-center text-white">
                        <Heartbeat size={22} weight="bold" />
                      </div>
                      <span
                        className={`text-xs font-bold uppercase tracking-widest px-2.5 py-1 rounded-full ${
                          full ? "text-red-600 bg-red-50" : spots <= 2 ? "text-amber-600 bg-amber-50" : "text-green-600 bg-green-50"
                        }`}
                        data-testid={`class-spots-${c.id}`}
                      >
                        {full ? "Full" : `${spots} left`}
                      </span>
                    </div>
                    <h3 className="font-heading text-lg font-black mt-4">{c.title}</h3>
                    <div className="mt-2 space-y-1 text-sm text-zinc-500">
                      <p className="flex items-center gap-2"><Clock size={15} weight="bold" /> {c.time}{c.end ? ` – ${c.end}` : ""}</p>
                      {c.coach && <p className="flex items-center gap-2"><User size={15} weight="bold" /> {c.coach}</p>}
                      {capacity > 0 && <p className="flex items-center gap-2"><Users size={15} weight="bold" /> {booked}/{capacity} booked</p>}
                    </div>
                    <a
                      href={GYMMASTER_BOOK_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`book-class-${c.id}`}
                      aria-disabled={full}
                      onClick={(e) => { if (full) e.preventDefault(); }}
                      className={`mt-4 w-full inline-flex items-center justify-center gap-2 rounded-sm py-2.5 font-bold transition-colors ${
                        full ? "bg-zinc-100 text-zinc-400 cursor-not-allowed pointer-events-none" : "bg-zinc-950 text-white hover:bg-zinc-800"
                      }`}
                    >
                      {full ? "Fully booked" : (<>Book on GymMaster <ArrowRight size={15} weight="bold" /></>)}
                    </a>
                  </motion.div>
                );
              })}
            </div>
          </div>
        ))}
      </section>

      <footer className="bg-zinc-950 text-white py-14">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="font-heading font-black text-lg">OTOROHANGA<span className="text-[#FF4F00]">.</span> HEALTH & FITNESS</div>
          <p className="text-sm text-zinc-500">Questions? Email Erin@otofitness.co.nz or call 021 154 1728</p>
        </div>
      </footer>

      <AuthModal open={authOpen} onOpenChange={setAuthOpen} initialMode="register" onSuccess={() => navigate("/dashboard")} />
    </div>
  );
}
